import { Component, DestroyRef, Input, OnChanges, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  CSVExportService,
  CardComponent,
  FormatDatetimeTimezonePipe,
  FormatNumberPipe,
  FormatterService,
  MessengerService,
  __,
  blueZodiac,
  cantaloupe,
  pastelGreen,
  rainDrop,
  randomString,
} from '@web/birdz-angular';
import { LegendItemComponent } from 'app/components/legend-item/legend-item.component';
import { standardColors } from 'app/config/standard-colors.config';
import { EquippingStats } from 'app/interfaces/equipping-stats.interface';
import chroma from 'chroma-js';
import { format } from 'date-fns';
import Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { tap } from 'rxjs';
import { fontFamily } from '../../config/constants.config';
import { RemoteReadingStats } from '../../interfaces/remote-reading-stats.interface';
import { ModalHelpRemoteReadingComponent } from '../modal-help-remote-reading/modal-help-remote-reading.component';

@Component({
  selector: 'app-card-remote-reading',
  templateUrl: './card-remote-reading.component.html',
  styleUrls: ['./card-remote-reading.component.scss'],
  imports: [
    MatButtonModule,
    MatIconModule,
    HighchartsChartComponent,
    TranslocoPipe,
    MatTooltipModule,
    FormatNumberPipe,
    FormatDatetimeTimezonePipe,
    FormsModule,
    CardComponent,
    MatSlideToggleModule,
    LegendItemComponent,
  ],
})
export class CardRemoteReadingComponent implements OnChanges, OnInit {
  private transloco = inject(TranslocoService);
  private formatter = inject(FormatterService);
  private dialog = inject(MatDialog);
  private csvExport = inject(CSVExportService);
  private messenger = inject(MessengerService);

  private destroyRef = inject(DestroyRef);
  @Input() remoteReadingStats?: RemoteReadingStats | null = null;
  @Input() remoteReadingStatsLastModified?: Date | null = null;
  @Input() equippingStats?: EquippingStats | null = null;
  @Input({ required: true }) hasPerimeter?: boolean;
  @Input({ required: true }) isAvailableForSelectedTecho?: boolean;
  @Input({ required: true }) isLoading?: boolean;
  chartOptions?: Highcharts.Options | null;
  isDetailedView = false;
  detailedLegendItems: Record<
    string,
    {
      id: string;
      title: string;
    }[]
  > = {};
  detailedLegendChildren: Record<string, string[]> = {};
  Highcharts = Highcharts;
  updateFlag = false;
  chart?: Highcharts.Chart;
  categoryTranslations: Record<string, string> = {
    OK: 'CONNECTED_FUNCTIONAL',
    NC: 'CONNECTED_NONFUNCTIONAL_NC',
    HS: 'CONNECTED_NONFUNCTIONAL_HS',
    NON_RACCORDE: 'NOT_CONNECTED',
  };
  categoryColors: Record<string, string> = {
    OK: pastelGreen,
    NC: blueZodiac,
    HS: rainDrop,
    NON_RACCORDE: cantaloupe,
  };
  categoryDataKey: Record<
    string,
    | 'devices_connected_functional'
    | 'devices_connected_nonfunctional_nc'
    | 'devices_connected_nonfunctional_hs'
    | 'devices_not_connected'
  > = {
    OK: 'devices_connected_functional',
    NC: 'devices_connected_nonfunctional_nc',
    HS: 'devices_connected_nonfunctional_hs',
    NON_RACCORDE: 'devices_not_connected',
  };

  get total(): number {
    return (
      (this.remoteReadingStats?.devices_connected_nonfunctional ?? 0) +
      (this.remoteReadingStats?.devices_connected_functional ?? 0) +
      (this.remoteReadingStats?.devices_not_connected ?? 0)
    );
  }

  ngOnInit() {
    this.handleLanguageChange();
  }

  ngOnChanges() {
    this.updateChart();
  }

  setChartInstance(chart: Highcharts.Chart) {
    this.chart = chart;
  }

  help() {
    this.dialog.open(ModalHelpRemoteReadingComponent, { maxWidth: '1000px' });
  }

  handleLanguageChange() {
    this.transloco.langChanges$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.updateChart())
      )
      .subscribe();
  }

  resetChart() {
    this.chartOptions = null;
  }

  toggleView() {
    this.updateChart();
  }

  subtitle = (count?: number, total?: number) => {
    count = count ?? 0;

    const METERS = __('METERS', { count });
    const percentage = total != null ? (count / total) * 100 : Infinity;
    return `${this.formatter.number(count)} ${METERS} - ${this.formatter.number(percentage, '0.0-1')}%`;
  };

  getData() {
    const connectedFunctional = {
      id: 'connected_functional',
      name: __(this.categoryTranslations['OK']),
      color: this.categoryColors['OK'],
      y: this.remoteReadingStats ? (this.remoteReadingStats[this.categoryDataKey['OK']] ?? 0) : null,
    };

    const connectedNonFunctionalNC = {
      id: 'connected_non_functional_nc',
      name: __(this.categoryTranslations['NC']),
      color: this.categoryColors['NC'],
      y: this.remoteReadingStats ? (this.remoteReadingStats[this.categoryDataKey['NC']] ?? 0) : null,
    };

    const connectedNonFunctionalHS = {
      id: 'connected_non_functional_hs',
      name: __(this.categoryTranslations['HS']),
      color: this.categoryColors['HS'],
      y: this.remoteReadingStats ? (this.remoteReadingStats[this.categoryDataKey['HS']] ?? 0) : null,
    };

    const notConnected = {
      id: 'not_connected',
      name: __(this.categoryTranslations['NON_RACCORDE']),
      color: this.categoryColors['NON_RACCORDE'],
      y: this.remoteReadingStats ? (this.remoteReadingStats[this.categoryDataKey['NON_RACCORDE']] ?? 0) : null,
    };

    this.detailedLegendItems = {};
    this.detailedLegendChildren = {};
    const equippedMetersByStateType: {
      id: string;
      name: string;
      color: string;
      y: number;
    }[] = [];

    if (this.equippingStats?.pds_equipped_by_state_type) {
      Object.keys(this.equippingStats.pds_equipped_by_state_type).forEach((state) => {
        if (!['OK', 'NC', 'HS', 'NON_RACCORDE'].includes(state)) {
          return;
        }

        this.detailedLegendItems[state] = [];
        this.detailedLegendChildren[state] = [];

        this.getSortedEquippedByTypeEntries(state).forEach(([type, count], index) => {
          const id = 'equipped_meters_' + state + '_' + type;

          equippedMetersByStateType.push({
            id: id,
            name: `${__(this.categoryTranslations[state])} (${type})`,
            color: chroma.mix(this.categoryColors[state], standardColors[index], 0.5).hex(),
            y: count,
          });

          this.detailedLegendChildren[state].push(id);
          this.detailedLegendItems[state].push({
            id: id,
            title: __(type),
          });
        });
      });
    }

    return this.isDetailedView
      ? [...equippedMetersByStateType]
      : [connectedFunctional, connectedNonFunctionalNC, connectedNonFunctionalHS, notConnected];
  }

  getSortedEquippedByTypeEntries(state: string) {
    return Object.entries(this.equippingStats?.pds_equipped_by_state_type[state] ?? {}).sort((a, b) =>
      a[1] < b[1] ? 1 : -1
    ); // sort count descending
  }

  getLegendItemChildren(state: string): string[] {
    return this.isDetailedView ? this.detailedLegendChildren[state] : [];
  }

  updateChart() {
    if (!this.remoteReadingStats) {
      return this.resetChart();
    }
    const data = this.getData();
    this.chartOptions = {
      credits: {
        enabled: false,
      },
      title: {
        text: undefined,
      },
      chart: {
        plotBackgroundColor: undefined,
        plotBorderWidth: undefined,
        plotShadow: false,
        type: 'pie',
        style: {
          fontFamily: fontFamily,
        },
      },
      tooltip: {
        pointFormat: `${__('COUNT')}${__('COLON')}{point.y}`,
      },
      lang: {
        noData: '',
      },
      noData: {},
      legend: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          allowPointSelect: false,
          cursor: 'pointer',
          dataLabels: {
            enabled: false,
          },
          showInLegend: true,
        },
      },
      series: [
        {
          type: 'pie',
          data: data,
        },
      ],
    };
    setTimeout(() => {
      if (this.chartOptions) {
        this.chart?.update(this.chartOptions);
        this.updateFlag = !this.updateFlag;
      }
    });
  }

  export() {
    if (!this.remoteReadingStats) {
      this.messenger.error(__('NO_DATA_TO_EXPORT'));
      return;
    }

    const data: (string | number | null)[][] = [];
    const lastModified = this.formatter.datetime(this.remoteReadingStatsLastModified ?? new Date());
    const getPercentage = (count: number) => Math.round((count / (this.total > 0 ? this.total : 1)) * 100 * 10) / 10;

    data.push([__('DATE'), __('ENTITY_NAME'), __('MODEL_FAMILY'), __('NUMBER_OF_METERS'), __('PERCENTAGE')]);
    data.push([lastModified, __('REMOTE_READ_DEVICE_COUNT'), null, this.total, 100]);

    ['OK', 'NC', 'HS', 'NON_RACCORDE'].forEach((state) => {
      const stateTotal = (
        this.remoteReadingStats ? (this.remoteReadingStats[this.categoryDataKey[state]] ?? 0) : 0
      ) as number;
      const stateTranslation = __(this.categoryTranslations[state]);
      data.push([lastModified, stateTranslation, __('ALL'), stateTotal, getPercentage(stateTotal)]);
      this.getSortedEquippedByTypeEntries(state).forEach(([type, count]) => {
        data.push([lastModified, stateTranslation, __(type), count, getPercentage(count)]);
      });
    });

    const datetime = format(new Date(), 'yyyy_MM_dd_HH_mm_ss');
    const id = randomString(10);
    const filename = `remote_read_Birdz_PoS_${datetime}_${id}`;
    const separator = this.formatter.config.csvSeparator ?? ';';

    this.csvExport.downloadFile(data, filename, separator);
  }
}
